import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FeedBadge } from './FeedBadge';
import type { BadgeType } from './FeedBadge';
import { formatRelativeTime } from '../../hooks/use-relative-time';
import styles from './WagerFeedCard.module.css';

type WagerFeedCardProps = {
  readonly wagerId: string;
  readonly status: string;
  readonly statement: string;
  readonly category: string;
  readonly creator: string;
  readonly creatorId: string;
  readonly opponent: string | null;
  readonly opponentId: string | null;
  readonly stake: number;
  readonly opponentStake: number | null;
  readonly timestamp: string;
  readonly resolution: { readonly winnerName: string; readonly payout: number; readonly summary: string } | null;
};

const statusToBadge = (status: string): BadgeType => {
  if (status === 'resolved' || status === 'awaiting_claim') return 'resolved';
  if (status === 'matched') return 'conflict';
  return 'offer';
};

const WagerFeedCard = ({ wagerId, status, statement, category, creator, creatorId, opponent, opponentId, stake, opponentStake, timestamp, resolution }: WagerFeedCardProps): React.ReactElement => {
  const navigate = useNavigate();
  const badgeType = statusToBadge(status);
  const isMatched = status === 'matched' || status === 'challenged' || status === 'evidence_submitted';
  const isResolved = status === 'awaiting_claim' || status === 'resolved';

  const subtitleMap: Record<string, string> = {
    matched: 'Active Conflict',
    challenged: 'Challenged',
    evidence_submitted: 'Evidence Submitted',
    awaiting_claim: 'Resolved — Awaiting Claim',
    resolved: 'Resolved',
  };

  return (
    <div
      className={`${styles.card} ${styles[badgeType]}`}
      onClick={(): void => { void navigate(`/wager/${wagerId}`); }}
      role="link"
      tabIndex={0}
      data-testid="wager-card"
    >
      <FeedBadge type={badgeType} />
      {(isMatched || isResolved) ? <div className={styles.subtitle}>{subtitleMap[status] ?? status}</div> : null}
      {!isMatched && !resolution && creator ? (
        <div className={styles.opener}>
          <span
            className={styles.openerName}
            onClick={(e): void => { e.stopPropagation(); void navigate(`/entity/${creatorId}`); }}
            role="link"
            tabIndex={0}
            data-testid="wager-card-creator"
          >
            {creator}
          </span>
          <span className={styles.openerLabel}> opened a wager</span>
        </div>
      ) : null}
      <div className={styles.statement}>{statement}</div>
      {(isMatched || isResolved) ? (
        <div className={styles.vs}>
          <span
            className={styles.vsSide}
            onClick={(e): void => { e.stopPropagation(); void navigate(`/entity/${creatorId}`); }}
            role="link"
            tabIndex={0}
            data-testid="wager-card-creator"
          >
            {creator}
          </span>
          <span className={styles.vsStake}>{stake} MC</span>
          <span className={styles.vsDivider}>VS</span>
          <span
            className={styles.vsSide}
            onClick={(e): void => { e.stopPropagation(); void navigate(`/entity/${opponentId}`); }}
            role="link"
            tabIndex={0}
            data-testid="wager-card-opponent"
          >
            {opponent}
          </span>
          <span className={styles.vsStake}>{opponentStake ?? 0} MC</span>
        </div>
      ) : (
        <div className={styles.stake}>
          <span className={`${styles.stakeAmount} ${styles[`stakeAmount_${badgeType}`]}`}>{stake} MC</span>
          <span className={styles.stakeLabel}>at stake</span>
        </div>
      )}
      {resolution ? (
        <div className={styles.resolution}>{resolution.winnerName} wins {resolution.payout} MC — {resolution.summary}</div>
      ) : null}
      <div className={styles.meta}>
        <span>{category}</span>
        {!isMatched && !isResolved && !resolution ? (
          <>
            <span className={styles.sep}>·</span>
            <span className={styles.awaiting}>awaiting challenger</span>
          </>
        ) : null}
        <span className={styles.sep}>·</span>
        <span>{formatRelativeTime(timestamp)}</span>
      </div>
      {!resolution ? (
        <div className={styles.promptRow}>
          <button type="button" className={styles.promptBtn} onClick={(e): void => { e.stopPropagation(); void navigate(`/wager/${wagerId}`); }} data-testid="wager-card-prompt-why">why this side?</button>
          <button type="button" className={styles.promptBtn} onClick={(e): void => { e.stopPropagation(); void navigate(`/wager/${wagerId}`); }} data-testid="wager-card-prompt-evidence">evidence?</button>
          <button type="button" className={styles.promptBtn} onClick={(e): void => { e.stopPropagation(); void navigate(`/wager/${wagerId}`); }} data-testid="wager-card-prompt-challenge">challenge</button>
        </div>
      ) : null}
    </div>
  );
};

export { WagerFeedCard };
